// File: ReportFourthSection.js
import { useRef, useEffect, useState } from "react";
import "./ReportFourthSection.css";
import { FaMinusCircle } from "react-icons/fa";
import { CgAdd } from "react-icons/cg";
import { FaCircle } from "react-icons/fa";
import { RiLightbulbFlashLine } from "react-icons/ri";

export default function ReportFourthSection({ res }) {
  const maxArcScore = 600;
  
  const clampedArcScore = Math.max(
    0,
    Math.min(maxArcScore, Math.round(res.totalScore))
  );
  const arcFraction = clampedArcScore / maxArcScore;

  /* ---------------- COLORS (same as ScoreCard) ---------------- */
  const getColorsByScore = (s) => {
    if (s >= 500)
      return { stroke: "#18a558", halo: "rgba(24,165,88,0.14)" };
    if (s >= 400)
      return { stroke: "#7fde9a", halo: "rgba(127,222,154,0.14)" };
    if (s >= 300)
      return { stroke: "#ffc66a", halo: "rgba(255,198,106,0.14)" };
    return { stroke: "#ff6b6b", halo: "rgba(255,107,107,0.14)" };
  };

  const color = getColorsByScore(clampedArcScore);

  /* ---------------- REMARKS ---------------- */
  const getRemarksAndAction = (s) => {
    if (s >= 500)
      return {
        remarks:
          "Validated, Visionary, Distinction. Investment-Ready / Incubation Stage.",
        action:
          "Exceptional innovation with strong validation. Ready for incubation, investors, or funding rounds.",
      };

    if (s >= 400)
      return {
        remarks:
          "Promising Potential, Positive. Validation Completed / Pre-Incubation.",
        action:
          "Strong fundamentals with minor refinement needed in GTM or pricing.",
      };

    if (s >= 300)
      return {
        remarks: "Needs Work, Neutral, Idea Stage / Prototype Potential.",
        action:
          "Requires improved validation depth, clearer metrics, and MVP testing.",
      };

    return {
      remarks: "High Risk / Invalid Idea, Negative.",
      action:
        "Major rework or pivot required before validation or funding attempts.",
    };
  };

  const { remarks, action } = getRemarksAndAction(clampedArcScore);

  /* ---------------- SVG GEOMETRY ---------------- */
  const viewW = 240;
  const viewH = 330;
  const cx = 75;
  const cy = 165;
  const greyR = 150;
  const arcR = 160;

  /* ---------------- ARC LENGTH FIX ---------------- */
  const svgRef = useRef(null);
  const [arcLen, setArcLen] = useState(0);
  const [animStarted, setAnimStarted] = useState(false);

  useEffect(() => {
    const path = svgRef.current?.querySelector(".arc-path");
    if (path) {
      setArcLen(path.getTotalLength());
    }

    const t = setTimeout(() => setAnimStarted(true), 80);
    return () => clearTimeout(t);
  }, []);

  const targetLen = arcLen * arcFraction;

  /* ---------------- SCORE CARD POSITION ---------------- */
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const CARD_W = 120;
  const CARD_H = 68;
  const OUTWARD_PIXELS = 8;
  const CARD_INSIDE_FACTOR = 0.28;

  useEffect(() => {
    function compute() {
      if (!svgRef.current) return;

      const svgRect = svgRef.current.getBoundingClientRect();
      const parentRect = svgRef.current.parentElement.getBoundingClientRect();
      const scale = svgRect.width / viewW;

      const angleDeg = -90 + arcFraction * 180;
      const angleRad = (angleDeg * Math.PI) / 180;

      const endSvgX = cx + arcR * Math.cos(angleRad);
      const endSvgY = cy + arcR * Math.sin(angleRad);
      const endPxX = endSvgX * scale;
      const endPxY = endSvgY * scale;

      const nx = Math.cos(angleRad);
      const ny = Math.sin(angleRad);

      const movedX = endPxX + nx * OUTWARD_PIXELS;
      const movedY = endPxY + ny * OUTWARD_PIXELS;

      let centerX =
        svgRect.left -
        parentRect.left +
        movedX -
        CARD_W * CARD_INSIDE_FACTOR;
      let centerY = svgRect.top - parentRect.top + movedY;

      const pad = 6;
      centerX = Math.max(
        -CARD_W * 0.28,
        Math.min(centerX, parentRect.width - CARD_W - pad)
      );
      centerY = Math.max(
        pad + CARD_H / 2,
        Math.min(centerY, parentRect.height - CARD_H / 2 - pad)
      );

      setPos({ x: centerX, y: centerY });
    }

    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [arcFraction]);

  const cardStyle = {
    left: `${pos.x}px`,
    top: `${pos.y}px`,
    transform: "translateY(-50%)",
  };

  return (
    <section className="r4-section">
      <div className="r4-top">
        <div className="r4-left">
          <svg
            ref={svgRef}
            className="r4-gauge r4-gauge-vertical"
            viewBox={`0 0 ${viewW} ${viewH}`}
            preserveAspectRatio="xMinYMid meet"
          >
            <defs>
              <filter id="glowV" x="-80%" y="-60%" width="220%" height="220%">
                <feGaussianBlur stdDeviation="6" result="b" />
                <feMerge>
                  <feMergeNode in="b" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Grey background */}
            <path
              d={`M ${cx} ${cy - greyR} A ${greyR} ${greyR} 0 0 1 ${cx} ${
                cy + greyR
              } L ${cx} ${cy} Z`}
              fill="#efefef"
            />

            {/* Halo arc */}
            <path
              className="arc-path"
              d={`M ${cx} ${cy - arcR} A ${arcR} ${arcR} 0 0 1 ${cx} ${
                cy + arcR
              }`}
              style={{
                stroke: color.halo,
                strokeWidth: 14,
                fill: "none",
                filter: "url(#glowV)",
                strokeDasharray: arcLen,
                strokeDashoffset: animStarted
                  ? arcLen - targetLen
                  : arcLen,
                transition: "stroke-dashoffset 850ms ease-out",
              }}
            />

            {/* Main arc */}
            <path
              className="arc-path"
              d={`M ${cx} ${cy - arcR} A ${arcR} ${arcR} 0 0 1 ${cx} ${
                cy + arcR
              }`}
              style={{
                stroke: color.stroke,
                strokeWidth: 9,
                fill: "none",
                filter: "url(#glowV)",
                strokeDasharray: arcLen,
                strokeDashoffset: animStarted
                  ? arcLen - targetLen
                  : arcLen,
                transition:
                  "stroke-dashoffset 900ms cubic-bezier(.22,.9,.3,1)",
              }}
            />
          </svg>

          <div
            className={`r4-score-box ${animStarted ? "anim" : ""}`}
            style={cardStyle}
          >
            <div className="r4-score-number">{res.totalScore}</div>
            <div className="r4-score-label">YOUR SCORE</div>
          </div>
        </div>

        {/* SCORE LEVELS */}
        <div className="r4-middle-score">
          <ul className="r4-score-list">
            <li>
              <RiLightbulbFlashLine size={18} color="#18a558" />
              <span className="level-text"> VALIDATED & VISIONARY</span>
              <span className="level-range">500–600</span>
            </li>
            <li>
              <CgAdd size={18} color="#7fde9a" />
              <span className="level-text"> PROMISING POTENTIAL</span>
              <span className="level-range">400–499</span>
            </li>
            <li>
              <FaCircle size={14} color="#ffc66a" />
              <span className="level-text"> NEEDS WORK</span>
              <span className="level-range">300–399</span>
            </li>
            <li>
              <FaMinusCircle size={16} color="#ff6b6b" />
              <span className="level-text"> HIGH RISK</span>
              <span className="level-range">&lt; 300</span>
            </li>
          </ul>
        </div>
      </div>

      {/* REMARKS */}
      <div className="r4-bottom">
        <h3 className="r4-heading">Remarks & Recommended Actions</h3>
        <table className="r4-table">
          <thead>
            <tr>
              <th>REMARKS SUMMARY</th>
              <th>RECOMMENDED ACTION</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{remarks}</td>
              <td>{action}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* AI RECOMMENDATIONS */}
      {((res?.sectionScores?.aiReport?.recommendations?.length > 0) ||
        (res?.sectionScores?.ai_report?.recommendations?.length > 0) ||
        (res?.aiReport?.recommendations?.length > 0)) && (
        <div className="r4-ai-recommendations" style={{ marginTop: "25px" }}>
          <h3 className="r4-heading">Personalized AI Action Plan</h3>
          <div className="r4-rec-list">
            {(res?.sectionScores?.aiReport?.recommendations ||
              res?.sectionScores?.ai_report?.recommendations ||
              res?.aiReport?.recommendations).map((rec, idx) => {
              // Handle both old format (string) and new format ({text, priority, module})
              const recText   = typeof rec === "string" ? rec : (rec?.text ?? rec);
              const priority  = typeof rec === "object" ? rec?.priority : null;
              const badgeStyle = priority === "High"
                ? { background: "#e74c3c" }
                : priority === "Medium"
                ? { background: "#f39c12" }
                : {};
              const badgeLabel = priority ? `${priority} Priority` : `Action ${idx + 1}`;
              return (
                <div className="r4-rec-item" key={idx}>
                  <span className="r4-rec-badge" style={badgeStyle}>{badgeLabel}</span>
                  <span className="r4-rec-text">{recText}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}
